'use client';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button/Button';
import Icon from '../Icon';
import { t } from '../../i18n';

import s from './MapFilterPanel.module.scss';

// $breakpoints M in variables.scss: the width the stylesheets stop stacking the
// panel above the map and start floating it over the corner.
const FLOATS_FROM = 768;

/**
 * Everything that narrows what a map shows, in one panel over the map.
 *
 * One panel rather than a control per kind of filter: a layer list is a filter
 * like a date range is, and a reader narrowing a map should not have to learn
 * which of two places each one lives in. Open on arrival wherever it floats
 * over the map, so what can be narrowed is readable at a glance; the sections
 * the caller hands in are `Dropdown`s, which is where collapsing one of them
 * lives.
 *
 * The whole panel folds away to its button in one click, which is what a reader
 * wants when they are done filtering and want the map back — folding it section
 * by section leaves the chrome behind and still covers the corner. The button is
 * the holdings map's and the reports archive's, count and all, so a reader who
 * has filtered one of those recognises it.
 *
 * Dressed like the holdings map's filter dropdown — the sections end in the
 * same dark Clear bar — so a reader who has filtered one map has filtered
 * them both.
 */
const MapFilterPanel = ({
    title,
    activeCount = 0,
    clearLabel = null,
    onClear = null,
    children,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    // Whether the panel is a band above the map rather than a float over its
    // corner, which is the stylesheets' decision — read here because the
    // button's shape follows it: the band's lid spans the band, a float's is
    // as wide as its own label. Floating is what the server and the first
    // client render agree on, so nothing here can mismatch on hydration.
    const [isStacked, setIsStacked] = useState(false);

    useEffect(() => {
        const sync = () => setIsStacked(window.innerWidth < FLOATS_FROM);

        sync();

        // Stacked, the panel costs the map the screen it is stacked above, so a
        // reader there gets the map first and the filters on a tap. Only this
        // first reading decides it: after that the state is the reader's, and a
        // resize taking it back would undo them.
        if (window.innerWidth < FLOATS_FROM) {
            setIsOpen(false);
        }

        window.addEventListener('resize', sync);

        return () => window.removeEventListener('resize', sync);
    }, []);

    return (
        <div className={s.Root}>
            <Button
                title={isOpen ? t('mapFilter.close') : t('mapFilter.filter')}
                modifier="FilterAlternative"
                size="Small"
                icon={isOpen ? 'FilterWhite' : 'Filter'}
                isActive={isOpen}
                isFullWidth={isStacked}
                // What is still narrowing the map while the panel is folded
                // away, so closing it never hides that it is.
                description={activeCount > 0 && `(${activeCount})`}
                aria-expanded={isOpen}
                onClick={() => setIsOpen((open) => !open)}
            />

            {isOpen && (
                <section className={s.Panel} aria-label={title}>
                    {children}

                    {/* Nothing until a filter is applied — there is nothing to
                        clear — then the dark bar the holdings filter ends in. */}
                    {onClear && activeCount > 0 && (
                        <div className={s.Actions}>
                            <button className={s.Action} onClick={onClear}>
                                {clearLabel}
                                <Icon name="Close" className={s.ActionIcon} />
                            </button>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
};

MapFilterPanel.propTypes = {
    // What the panel is, for readers who cannot see it: there is no visible
    // heading, the sections speak for themselves.
    title: PropTypes.string.isRequired,
    // How many filters are narrowing what is on the map — what decides whether
    // there is anything to clear, and what the folded button reports.
    activeCount: PropTypes.number,
    clearLabel: PropTypes.string,
    // Clears every filter at once. Omitted, no Clear bar is offered.
    onClear: PropTypes.func,
    children: PropTypes.node,
};

export default MapFilterPanel;
